import { useCallback, useMemo, useState } from "react";
import {
	AnyConfigResource,
	CreateNewRuleParams,
	NotFoundError,
	OrganizationPreferences,
	VelavuAPI,
	VelavuDeviceModel,
	VelavuRule,
	VelavuRuleCategory,
} from "velavu-js-api";
import { useDeviceModels } from "../main/device-model-provider";
import { useSignInContext } from "../sign-in-context/sign-in-context";
import { useCancellableEffect } from "../../helper/hook-helper";

export interface DynamicDeviceRateSettings {
	movingRate: number;
	cooldownRate: number;
}

export interface DeviceRateSettings {
	// The standard update rate for the device in seconds, or 0 if disabled
	standardRate: number;
	// The dynamic update settings for the device, or null if disabled
	dynamic: DynamicDeviceRateSettings | null;
}

export interface DeviceRateSettingsEntry {
	model: VelavuDeviceModel;
	settings: DeviceRateSettings;
}

export type DeviceSettingsLastAction = "save" | "reset";

export interface DeviceSettingsState {
	entries: DeviceRateSettingsEntry[] | null;
	isDirty: boolean;
	isSavingChanges: boolean;
	canResetToDefaults: boolean;
	lastSaveResult: boolean | null;
	lastAction: DeviceSettingsLastAction | null;
	updateSettings: (modelName: string, settings: DeviceRateSettings) => void;
	saveChanges: () => Promise<void>;
	resetToDefaults: () => Promise<void>;
}

export const DEFAULT_DEVICE_RATE_SETTINGS = {
	standardRate: 0,
	dynamic: {
		movingRate: 30,
		cooldownRate: 30,
	},
} as const satisfies DeviceRateSettings;

function areDynamicSettingsEqual(
	a: Readonly<DynamicDeviceRateSettings> | null,
	b: Readonly<DynamicDeviceRateSettings> | null,
): boolean {
	if (a === null && b === null) {
		return true;
	}
	if (a === null || b === null) {
		return false;
	}
	return a.movingRate === b.movingRate && a.cooldownRate === b.cooldownRate;
}

function areDeviceRateSettingsEqual(
	a: Readonly<DeviceRateSettings>,
	b: Readonly<DeviceRateSettings>,
): boolean {
	return (
		a.standardRate === b.standardRate &&
		areDynamicSettingsEqual(a.dynamic, b.dynamic)
	);
}

function areEntriesEqual(
	a: readonly Readonly<DeviceRateSettingsEntry>[],
	b: readonly Readonly<DeviceRateSettingsEntry>[],
): boolean {
	if (a.length !== b.length) {
		return false;
	}
	return a.every(
		(entry, index) =>
			entry.model.name === b[index].model.name &&
			areDeviceRateSettingsEqual(entry.settings, b[index].settings),
	);
}

function configToDeviceRateSettings(
	config: AnyConfigResource,
): DeviceRateSettings {
	if (config.positioning === undefined) {
		return DEFAULT_DEVICE_RATE_SETTINGS;
	}

	//Resolve the standard update rate
	const standardRate =
		config.positioning.fixed_rate_s ??
		DEFAULT_DEVICE_RATE_SETTINGS.standardRate;

	//Resolve the dynamic update settings
	let dynamic: DynamicDeviceRateSettings | null;
	if (config.positioning.dynamic_mode) {
		dynamic = {
			movingRate:
				config.positioning.dynamic_rate_s ??
				DEFAULT_DEVICE_RATE_SETTINGS.dynamic.movingRate,
			cooldownRate:
				config.imu?.motion_cooldown_s ??
				DEFAULT_DEVICE_RATE_SETTINGS.dynamic.cooldownRate,
		};
	} else {
		dynamic = null;
	}

	return { standardRate, dynamic };
}

function ruleToDeviceRateSettings(rule: VelavuRule): DeviceRateSettings {
	//Make sure the device has a config object
	const config = rule.action.config;
	if (config === undefined) {
		return DEFAULT_DEVICE_RATE_SETTINGS;
	}

	return configToDeviceRateSettings(config);
}

function deviceRateSettingsToRule(
	settings: DeviceRateSettings,
	deviceModel: VelavuDeviceModel,
): CreateNewRuleParams {
	return {
		name: `Device rate settings for ${deviceModel.name}`,
		category: VelavuRuleCategory.Managed,
		priority: 99,
		condition: `model == '${deviceModel.name}'`,
		action: {
			config: {
				positioning: {
					fixed_rate_s: settings.standardRate,
					dynamic_mode: settings.dynamic !== null,
					dynamic_rate_s: settings.dynamic?.movingRate,
				},
				imu: {
					motion_cooldown_s: settings.dynamic?.cooldownRate,
				},
			},
		},
	};
}

/**
 * Creates or updates the managed rule for a device model.
 * Returns the new rule ID if a rule was created, otherwise undefined.
 */
async function upsertDeviceRateRule(
	settings: DeviceRateSettings,
	deviceModel: VelavuDeviceModel,
	existingRuleID: string | undefined,
): Promise<string | undefined> {
	const rule = deviceRateSettingsToRule(settings, deviceModel);

	if (existingRuleID !== undefined) {
		try {
			await VelavuAPI.rules.modifyExistingRule(existingRuleID, rule);
			return;
		} catch (error) {
			// If the error was a 404, fall through and create a new rule.
			// Otherwise, throw the error.
			if (!(error instanceof NotFoundError)) {
				throw error;
			}
		}
	}

	const newRule = await VelavuAPI.rules.createNewRule(rule);
	return newRule.id;
}

async function getDeviceRateSettings(
	deviceModel: string,
	defaultConfig: Readonly<AnyConfigResource>,
	organizationSettings: Readonly<OrganizationPreferences> | undefined,
): Promise<DeviceRateSettings> {
	//If we don't have enough information, return a default value
	if (
		organizationSettings === undefined ||
		organizationSettings.rules === undefined
	) {
		return configToDeviceRateSettings(defaultConfig);
	}

	//If the rule is not in the organization settings, return a default value
	if (!(deviceModel in organizationSettings.rules)) {
		return configToDeviceRateSettings(defaultConfig);
	}

	//Get the rule for the device model
	const ruleID = organizationSettings.rules[deviceModel];

	//Get the rule from the backend
	let rule: VelavuRule;
	try {
		rule = await VelavuAPI.rules.getSpecificRule(ruleID);
	} catch (error) {
		if (error instanceof NotFoundError) {
			//Fall back to the default value
			return configToDeviceRateSettings(defaultConfig);
		} else {
			throw error;
		}
	}

	return ruleToDeviceRateSettings(rule);
}

export function useDeviceSettingsState(): DeviceSettingsState {
	//Request device models
	const { deviceModels } = useDeviceModels();

	//Request organization settings
	const userContext = useSignInContext();
	const organization = userContext.organization;
	const updateOrganization = userContext.updateOrganization;

	const [originalEntries, setOriginalEntries] = useState<
		DeviceRateSettingsEntry[] | null
	>(null);
	const [entries, setEntries] = useState<DeviceRateSettingsEntry[] | null>(
		null,
	);
	const [lastSaveResult, setLastSaveResult] = useState<boolean | null>(null);
	const [lastAction, setLastAction] =
		useState<DeviceSettingsLastAction | null>(null);

	const canResetToDefaults = useMemo(() => {
		const rules = organization?.settings?.rules;
		return rules !== undefined && Object.keys(rules).length > 0;
	}, [organization]);

	useCancellableEffect(
		(addPromise) => {
			//Wait until we have all the data we need
			if (deviceModels === null || organization === undefined) {
				return;
			}

			//Join device models and organization settings
			addPromise(
				Promise.all(
					deviceModels
						.map((deviceModel) => {
							//If this device doesn't have a default config, it's not supported
							if (deviceModel.default_config === undefined) {
								return null;
							}

							//Get the device rate settings
							return getDeviceRateSettings(
								deviceModel.name,
								deviceModel.default_config,
								organization.settings,
							).then((settings): DeviceRateSettingsEntry => {
								return { model: deviceModel, settings };
							});
						})
						.filter((entry) => entry !== null),
				),
			)
				.then((loadedEntries) => {
					setOriginalEntries(loadedEntries);
					setEntries(loadedEntries);
				})
				.catch((error) => {
					console.error(error);
				});
		},
		[deviceModels, organization],
	);

	const isDirty = useMemo(() => {
		if (entries === null || originalEntries === null) {
			return false;
		}
		return !areEntriesEqual(entries, originalEntries);
	}, [entries, originalEntries]);

	const updateSettings = useCallback(
		(modelName: string, settings: DeviceRateSettings) => {
			setEntries((prev) => {
				if (prev === null) {
					return null;
				}
				return prev.map((entry) =>
					entry.model.name === modelName
						? { ...entry, settings }
						: entry,
				);
			});
		},
		[],
	);

	const [isSavingChanges, setIsSavingChanges] = useState(false);
	const saveChanges = useCallback(async () => {
		//Make sure we have the needed information
		if (
			entries === null ||
			originalEntries === null ||
			organization === undefined ||
			isSavingChanges
		) {
			return;
		}
		setIsSavingChanges(true);
		setLastAction("save");

		try {
			//Update the rules for each device model
			const createdRules = await Promise.all(
				entries
					//Filter out entries that haven't changed
					.filter((entry, index) => {
						return !areDeviceRateSettingsEqual(
							entry.settings,
							originalEntries[index].settings,
						);
					})
					//Create or update the rule for each changed entry
					.map(async (entry) => {
						const lookupKey = entry.model.name;
						const existingRuleID =
							organization.settings?.rules?.[lookupKey];
						const newRuleID = await upsertDeviceRateRule(
							entry.settings,
							entry.model,
							existingRuleID,
						);
						if (newRuleID !== undefined) {
							return {
								lookupKey,
								newRuleID,
							};
						}
					}),
			);

			//Reduce the created rules to an object
			const createdRulesObject = createdRules
				.filter((rule) => rule !== undefined)
				.reduce<Record<string, string>>((acc, rule) => {
					acc[rule.lookupKey] = rule.newRuleID;
					return acc;
				}, {});

			//Update the organization-saved rule IDs if they changed
			if (Object.keys(createdRulesObject).length !== 0) {
				await updateOrganization({
					settings: {
						rules: createdRulesObject,
					},
				});
			}

			setOriginalEntries(entries);
			setLastSaveResult(true);
		} catch (error) {
			console.error(error);
			setLastSaveResult(false);
		} finally {
			setIsSavingChanges(false);
		}
	}, [
		entries,
		originalEntries,
		organization,
		updateOrganization,
		isSavingChanges,
	]);

	const resetToDefaults = useCallback(async () => {
		if (entries === null || organization === undefined || isSavingChanges) {
			return;
		}

		const ruleIDs = Object.values(organization.settings?.rules ?? {});
		if (ruleIDs.length === 0) {
			return;
		}

		setIsSavingChanges(true);
		setLastAction("reset");

		try {
			await Promise.all(
				ruleIDs.map(async (ruleID) => {
					try {
						await VelavuAPI.rules.deleteRule(ruleID);
					} catch (error) {
						if (!(error instanceof NotFoundError)) {
							throw error;
						}
					}
				}),
			);

			await updateOrganization({
				settings: {
					rules: undefined,
				},
			});

			const defaultEntries = entries.map((entry) => ({
				model: entry.model,
				settings: configToDeviceRateSettings(
					entry.model.default_config!,
				),
			}));
			setEntries(defaultEntries);
			setOriginalEntries(defaultEntries);
			setLastSaveResult(true);
		} catch (error) {
			console.error(error);
			setLastSaveResult(false);
		} finally {
			setIsSavingChanges(false);
		}
	}, [entries, organization, updateOrganization, isSavingChanges]);

	return {
		entries,
		isDirty,
		isSavingChanges,
		canResetToDefaults,
		lastSaveResult,
		lastAction,
		updateSettings,
		saveChanges,
		resetToDefaults,
	};
}
